import { useEffect, useMemo, useState } from 'react'
import { buildCorpus } from '../../../../../../shared/search/corpus'
import { createQueryCache } from '../../../../../../shared/search/query-cache'
import { buildResultsAndCounts, evaluateQuery, RESULT_LIMIT, TYPE_ALL, TYPE_COURSES, TYPE_FOLDERS } from '../../../../../../shared/search/search-index'

// The viewer's timing (spec §5.1): 50 ms after the last keystroke, scored in
// the browser over the list the page already loaded; the last 32 queries of
// that list are kept, so backspacing and chip clicks never score again.
export const SEARCH_DEBOUNCE_MS = 50

export const useNodeSearch = ({ items, kinds, query }) => {
  // The corpus lives in memory only, for this list; a reload (an edit, an
  // organization switch) builds a new one and a new cache.
  const corpus = useMemo(() => buildCorpus(items, kinds), [items, kinds])
  const cache = useMemo(() => createQueryCache(), [corpus]) // eslint-disable-line react-hooks/exhaustive-deps

  const [scored, setScored] = useState(query)
  useEffect(
    () => {
      if (query === scored) return
      // Clearing is immediate: the grid comes straight back.
      if (!query.trim()) {
        setScored(query)
        return
      }
      const timer = setTimeout(() => setScored(query), SEARCH_DEBOUNCE_MS)
      return () => clearTimeout(timer)
    },
    [query, scored]
  )

  const [type, setType] = useState(TYPE_ALL)
  const [all, setAll] = useState(false)
  const [active, setActive] = useState(0)

  // A new search starts at All, the first row and the first 50.
  useEffect(
    () => {
      if (!scored.trim()) setType(TYPE_ALL)
    },
    [scored]
  )
  useEffect(() => setAll(false), [scored, type])

  const set = useMemo(
    () => {
      if (!scored.trim()) return null
      let found = cache.get(scored)
      if (!found) {
        found = evaluateQuery(corpus, scored, Date.now())
        cache.put(scored, found)
      }
      return found
    },
    [scored, corpus, cache]
  )

  const { results, counts } = useMemo(() => buildResultsAndCounts(set, corpus, { type }, all ? -1 : RESULT_LIMIT), [set, corpus, type, all])

  useEffect(() => setActive(0), [results])

  const total = type === TYPE_COURSES ? counts.courses : type === TYPE_FOLDERS ? counts.folders : counts.all

  // The rows for what is typed right now, scored without waiting (Enter
  // pressed inside the 50 ms).
  const resultsNow = () => {
    if (!query.trim()) return []
    let found = cache.get(query)
    if (!found) {
      found = evaluateQuery(corpus, query, Date.now())
      cache.put(query, found)
    }
    return buildResultsAndCounts(found, corpus, { type }, RESULT_LIMIT).results
  }

  return {
    searching: !!scored.trim(),
    // The query the rows on screen are for (what was typed 50 ms ago).
    scoredQuery: scored.trim(),
    pending: query !== scored,
    resultsNow,
    results,
    counts,
    total,
    type,
    setType,
    showAll: () => setAll(true),
    active: results.length ? Math.min(active, results.length - 1) : -1,
    setActive,
    move: delta => setActive(a => Math.max(0, Math.min(results.length - 1, a + delta)))
  }
}
